<template lang="pug">
.numberInputComponent(:class="{ _disabled: isDisabled }")

  input.input(
    ref="inputRef"
    type="text"
    :value="displayValue"
    :disabled="isDisabled"
    inputmode="numeric"
    pattern="[0-9]*"
    @input="onInput"
    @blur="onBlur"
    @keydown.up.prevent="increase"
    @keydown.down.prevent="decrease"
    @wheel.prevent="onWheel"
  )

  .buttons
    ui-button(
      size="xs"
      @mousedown.prevent="startHold('dec')"
      @mouseup="stopHold"
      @mouseleave="stopHold"
      :is-disabled="isDisabled"
    )
      span.symbol -

    ui-button(
      size="xs"
      @mousedown.prevent="startHold('inc')"
      @mouseup="stopHold"
      @mouseleave="stopHold"
      :is-disabled="isDisabled"
    )
      span.symbol +
//- .numberHint Используйте кнопки +/−, колёсико мыши или клавиши ↑ ↓ для изменения значения
.numberHint
  | минимальное значение {{ min }}, максимальное значение {{ max }}
</template>

<script lang="ts" setup>
import { computed, ref, onBeforeUnmount } from 'vue'

interface Props {
  modelValue: number | null
  min?: number
  max?: number
  step?: number
  isDisabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  step: 1,
  min: undefined,
  max: undefined,
  isDisabled: false
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: number | null): void
}>()

const inputRef = ref<HTMLInputElement | null>(null)

const displayValue = computed(() => (props.modelValue !== null ? String(props.modelValue) : ''))

const clamp = (v: number) => {
  if (props.min !== undefined && v < props.min) return props.min
  if (props.max !== undefined && v > props.max) return props.max
  return v
}

const increase = () => {
  if (props.isDisabled) return
  const next = clamp((props.modelValue ?? 0) + props.step)
  emit('update:modelValue', next)
}

const decrease = () => {
  if (props.isDisabled) return
  const next = clamp((props.modelValue ?? 0) - props.step)
  emit('update:modelValue', next)
}

const onInput = (e: Event) => {
  if (props.isDisabled) return

  const target = e.target as HTMLInputElement

  const digits = target.value.replace(/[^0-9]/g, '')

  target.value = digits

  if (digits === '') {
    emit('update:modelValue', null)
    return
  }

  emit('update:modelValue', Number(digits))
}

const onBlur = () => {
  if (props.modelValue === null) return

  const clamped = clamp(props.modelValue)

  if (clamped !== props.modelValue) {
    emit('update:modelValue', clamped)
  }
}

const onWheel = (e: WheelEvent) => {
  if (props.isDisabled) return

  if (e.deltaY < 0) increase()
  else decrease()
}

let holdTimer: number | null = null
let holdInterval: number | null = null

const startHold = (type: 'inc' | 'dec') => {
  if (props.isDisabled) return

  const action = type === 'inc' ? increase : decrease

  action()

  holdTimer = window.setTimeout(() => {
    holdInterval = window.setInterval(action, 70)
  }, 400)
}

const stopHold = () => {
  if (holdTimer) {
    clearTimeout(holdTimer)
    holdTimer = null
  }

  if (holdInterval) {
    clearInterval(holdInterval)
    holdInterval = null
  }
}

onBeforeUnmount(stopHold)
</script>

<style lang="sass" scoped>
.numberInputComponent
  display: flex
  align-items: stretch
  gap: 2*$u

  .input
    flex: 1
    border: 1px solid transparent
    background: $colorBackground2
    padding: 2.25*$u
    border-radius: $brXS
    @include font(t14)

  .buttons
    display: flex
    gap: 1*$u
    align-items: center

  .symbol
    font-size: 20px
    font-weight: 700
    line-height: 1
    position: relative
    top: -1px

  &._disabled
    opacity: 0.6

.numberHint
  margin-top: 1*$u
  color: $colorGray2
  @include font(t12)
</style>
